import React, { useState, useEffect } from "react";

import { API_BASE_URL } from "../config";

import { MapPin, Plus, Edit2, Trash2, Check, X, Home, Phone, User } from "lucide-react";

const AddressBook = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        recipientName: "",
        phone: "",
        addressLine: "",
        district: "",
        province: "",
        postalCode: "",
        isDefault: false,
    });

    useEffect(() => {
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/addresses`, {
                headers: { Authorization: token },
            });
            if (!response.ok) throw new Error("Failed to fetch addresses");
            const data = await response.json();
            setAddresses(data);
        } catch (error) {
            console.error("Error fetching addresses:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const url = editingId
                ? `${API_BASE_URL}/api/addresses/${editingId}`
                : `${API_BASE_URL}/api/addresses`;

            const response = await fetch(url, {
                method: editingId ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error("Failed to save address");

            await fetchAddresses();
            resetForm();
        } catch (error) {
            console.error("Error saving address:", error);
            alert("บันทึกข้อมูลไม่สำเร็จ");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบที่อยู่นี้?")) return;

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/addresses/${id}`, {
                method: "DELETE",
                headers: { Authorization: token },
            });

            if (!response.ok) throw new Error("Failed to delete address");
            setAddresses(addresses.filter((addr) => addr.addressId !== id));
        } catch (error) {
            console.error("Error deleting address:", error);
            alert("ลบข้อมูลไม่สำเร็จ");
        }
    };

    const handleEdit = (address) => {
        setFormData({
            recipientName: address.recipientName || "",
            phone: address.phone || "",
            addressLine: address.addressLine || "",
            district: address.district || "",
            province: address.province || "",
            postalCode: address.postalCode || "",
            isDefault: address.isDefault || false,
        });
        setEditingId(address.addressId);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({
            recipientName: "",
            phone: "",
            addressLine: "",
            district: "",
            province: "",
            postalCode: "",
            isDefault: false,
        });
        setEditingId(null);
        setShowForm(false);
    };

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">กำลังโหลด...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-black rounded-lg">
                            <MapPin className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">สมุดที่อยู่</h1>
                            <p className="text-gray-500">จัดการที่อยู่สำหรับการจัดส่งสินค้า</p>
                        </div>
                    </div>

                    {!showForm && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all"
                        >
                            <Plus size={20} />
                            เพิ่มที่อยู่ใหม่
                        </button>
                    )}
                </div>

                {/* Add/Edit Form */}
                {showForm && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {editingId ? "แก้ไขที่อยู่" : "เพิ่มที่อยู่ใหม่"}
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Recipient Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        ชื่อผู้รับ
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="recipientName"
                                            value={formData.recipientName}
                                            onChange={handleChange}
                                            required
                                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        เบอร์โทรศัพท์
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address Line */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ที่อยู่
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 pointer-events-none">
                                        <Home className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        name="addressLine"
                                        value={formData.addressLine}
                                        onChange={handleChange}
                                        required
                                        rows={2}
                                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                                        placeholder="บ้านเลขที่, ซอย, ถนน, หมู่บ้าน"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {/* District */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        แขวง/ตำบล
                                    </label>
                                    <input
                                        type="text"
                                        name="district"
                                        value={formData.district}
                                        onChange={handleChange}
                                        required
                                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                    />
                                </div>

                                {/* Province */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        จังหวัด
                                    </label>
                                    <input
                                        type="text"
                                        name="province"
                                        value={formData.province}
                                        onChange={handleChange}
                                        required
                                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                    />
                                </div>

                                {/* Postal Code */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        รหัสไปรษณีย์
                                    </label>
                                    <input
                                        type="text"
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleChange}
                                        required
                                        maxLength={5}
                                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                    />
                                </div>
                            </div>

                            {/* Default Address */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    name="isDefault"
                                    id="isDefault"
                                    checked={formData.isDefault}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isDefault" className="text-sm text-gray-700">
                                    ตั้งเป็นค่าเริ่มต้น
                                </label>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={saving}
                                className="w-full flex items-center justify-center gap-3 py-4 px-8 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
                            >
                                {saving ? (
                                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Check size={20} />
                                        {editingId ? "บันทึกการเปลี่ยนแปลง" : "บันทึกข้อมูล"}
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}

                {/* Address List */}
                {addresses.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                        <MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">ยังไม่มีที่อยู่</h3>
                        <p className="text-gray-500">เพิ่มที่อยู่เพื่อความสะดวกในการสั่งซื้อสินค้า</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {addresses.map((address) => (
                            <div
                                key={address.addressId}
                                className={`bg-white rounded-xl shadow-sm border-2 p-6 transition-all ${address.isDefault ? "border-red-500" : "border-gray-100"
                                    }`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="font-semibold text-gray-900">{address.recipientName}</h3>
                                            {address.isDefault && (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                                    <Check size={12} />
                                                    ค่าเริ่มต้น
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-gray-600">{address.phone}</p>
                                        <p className="text-gray-600 mt-1">
                                            {address.addressLine}, {address.district}, {address.province} {address.postalCode}
                                        </p>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(address)}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(address.addressId)}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddressBook;
